import React, { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import API from '../../../services/API'
import useIsMobile from '../../../helpers/useIsMobile'

const getJourneyDay = (journey) =>
  journey?.pickup?.when?.exactTime ||
  journey?.pickup?.when?.patientReadyFrom ||
  journey?.dropoff?.when?.exactTime ||
  journey?.dropoff?.when?.targetArrivalTime ||
  null

const AssignJourneyDialog = ({ open, onClose, duties, onConfirm, journey }) => {
  const isPhone = useIsMobile('sm')
  const [selected, setSelected] = useState(null)
  const [suggestions, setSuggestions] = useState(null)
  const [unplacedReason, setUnplacedReason] = useState(null)
  const [loadingSuggestions, setLoadingSuggestions] = useState(false)

  useEffect(() => {
    if (!open) {
      setSelected(null)
      setSuggestions(null)
      setUnplacedReason(null)
    }
  }, [open])

  useEffect(() => {
    if (!open || !journey?._id) return undefined

    const day = getJourneyDay(journey)
    if (!day) return undefined

    let cancelled = false
    setLoadingSuggestions(true)

    API.get('/pts/unassigned-journey-duty-suggestions', {
      params: { date: moment(day).format('YYYY-MM-DD') },
    })
      .then((res) => {
        if (cancelled || res.status !== 200) return
        const byDutyId = new Map()
        ;(res.data?.suggestions || [])
          .filter((s) => String(s.journey?._id) === String(journey._id))
          .forEach((s) => byDutyId.set(String(s.duty?._id), s))
        setSuggestions(byDutyId)

        const unplaced = (res.data?.unplaced || []).find(
          (u) => String(u.journey?._id) === String(journey._id),
        )
        setUnplacedReason(unplaced?.reason || null)
      })
      .catch(() => {
        if (!cancelled) {
          setSuggestions(null)
          setUnplacedReason(null)
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingSuggestions(false)
      })

    return () => {
      cancelled = true
    }
  }, [open, journey])

  const sortedDuties = useMemo(() => {
    if (!suggestions) return duties
    return [...duties].sort((a, b) => {
      const scoreA = suggestions.get(String(a._id))?.score ?? -1
      const scoreB = suggestions.get(String(b._id))?.score ?? -1
      return scoreB - scoreA
    })
  }, [duties, suggestions])

  const selectedSuggestion = selected
    ? suggestions?.get(String(selected._id))
    : null
  const selectedWarnings = selectedSuggestion?.warnings || []
  const selectedHasNoSuggestion = selected && suggestions && !selectedSuggestion

  const getDutyLabel = (d) =>
    d
      ? `${d.vehicle?.callSign ?? 'No Vehicle'} • ${moment(
          d.start,
        ).format('HH:mm')}–${moment(d.end).format('HH:mm')}`
      : ''

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={isPhone}
    >
      <DialogTitle>Assign Journey to Duty</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {journey
              ? `${journey?.patient?.demographics?.firstName ?? ''} ${
                  journey?.patient?.demographics?.lastName ?? ''
                }`.trim()
              : ''}
          </Typography>

          {unplacedReason && (
            <Alert severity="warning">
              No duty is a clean fit for this journey: {unplacedReason} You can
              still assign manually below.
            </Alert>
          )}

          <Autocomplete
            options={sortedDuties}
            value={selected}
            onChange={(e, v) => setSelected(v)}
            loading={loadingSuggestions}
            getOptionLabel={getDutyLabel}
            isOptionEqualToValue={(a, b) => a._id === b._id}
            renderOption={(props, option) => {
              const suggestion = suggestions?.get(String(option._id))
              const hasWarnings = suggestion?.warnings?.length > 0
              return (
                <Box component="li" {...props} key={option._id}>
                  <Stack sx={{ width: '100%' }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="body2">
                        {getDutyLabel(option)}
                      </Typography>
                      {suggestion && !hasWarnings && (
                        <Chip
                          label="Good fit"
                          size="small"
                          color="success"
                          sx={{ height: 20 }}
                        />
                      )}
                      {hasWarnings && (
                        <Chip
                          label="Check first"
                          size="small"
                          color="warning"
                          sx={{ height: 20 }}
                        />
                      )}
                    </Stack>
                    {hasWarnings && (
                      <Typography variant="caption" color="text.secondary">
                        {suggestion.warnings.join(' · ')}
                      </Typography>
                    )}
                  </Stack>
                </Box>
              )
            }}
            renderInput={(params) => (
              <TextField {...params} label="Select duty" />
            )}
          />

          {selectedWarnings.length > 0 && (
            <Alert severity="warning">{selectedWarnings.join(' · ')}</Alert>
          )}
          {selectedHasNoSuggestion && (
            <Alert severity="info">
              This duty wasn't in today's auto-suggestions for this journey —
              double-check timing and vehicle/mobility fit before assigning.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="anBlue"
          disabled={!selected}
          onClick={() => selected && onConfirm(selected._id)}
        >
          Assign
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AssignJourneyDialog
